import type { LevelsResponse } from "@rmnlng/shared";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

// Re-export the shared shape so call sites can import everything related
// to progress from one module.
export type { LevelsResponse, LevelProgress, LevelStatus } from "@rmnlng/shared";

export type ProgressStats = {
  wordsTotalRo: number;
  wordsLearning: number;
  wordsMature: number;
  cardsDue: number;
  reviewsToday: number;
  reviewsThisWeek: number;
  streakDays: number;
  bestStreak: number;
  retentionPct: number;
  avgEase: number;
  reviewsPerDay: Array<{ date: string; count: number }>;
  gradeBreakdown: { again: number; hard: number; good: number; easy: number };
  totalStudySeconds: number;
};

/**
 * Compact duration formatter for the Home stat tile. Output needs to fit in a
 * ~80px-wide slot at 28px font, so we trade precision for width:
 *   - 0          → "0m"
 *   - <60s       → "<1m"
 *   - <60m       → "42m"
 *   - <10h       → "2h 15m"
 *   - ≥10h       → "12h"
 *
 * Deliberately not "Xd" past 24h — days feel inappropriate for a study
 * timer (sessions aren't continuous). "50h" is clearer than "2d 2h".
 */
export function formatStudyDuration(seconds: number): string {
  if (seconds <= 0) return "0m";
  if (seconds < 60) return "<1m";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 10) return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  return `${hours}h`;
}

export const progressKeys = {
  stats: ["progress", "stats"] as const,
  levels: ["progress", "levels"] as const,
};

export function useProgressStats() {
  return useQuery({
    queryKey: progressKeys.stats,
    queryFn: () => apiFetch<ProgressStats>("/progress/stats"),
    staleTime: 60_000,
  });
}

/**
 * Per-level vocab + grammar progress for the Levels page and the
 * by-level strip on Progress. Server returns all 7 CEFR levels in
 * canonical order — empty levels show as zeros, status "locked".
 *
 * Same `staleTime` as `useProgressStats` (1 min) — the underlying data
 * (mature card counts) shifts on every review submission, but the
 * query already invalidates from the review-submit mutation.
 */
export function useProgressLevels() {
  return useQuery({
    queryKey: progressKeys.levels,
    queryFn: () => apiFetch<LevelsResponse>("/progress/levels"),
    staleTime: 60_000,
  });
}
